import React, { useState, useEffect } from "react";
import { AiFillEye, AiFillGithub } from "react-icons/ai";
import { motion } from "framer-motion";
import { AppWrap, MotionWrap } from "../../wrapper";
import { urlFor, client } from "../../client";
import "./Work.scss";
import CaseStudyModal from "./CaseStudyModal";

const Work = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [isCaseStudyOpen, setIsCaseStudyOpen] = useState(false);
  const [activeCaseStudy, setActiveCaseStudy] = useState(null);

  const [animateCard, setAnimateCard] = useState({
    y: 0,
    opacity: 1,
  });

  const [works, setWorks] = useState([]);
  const [filterWork, setFilterWork] = useState([]);

  useEffect(() => {
    const query = '*[_type == "works"] | order(_updatedAt desc)';

    client.fetch(query).then((data) => {
      setWorks(data);
      setFilterWork(data);
    });
  }, []);

  const handleWorkFilter = (item) => {
    setActiveFilter(item);
    setAnimateCard([{ y: 100, opacity: 0 }]);

    setTimeout(() => {
      setAnimateCard([{ y: 0, opacity: 1 }]);

      if (item === "All") {
        setFilterWork(works);
      } else {
        setFilterWork(works.filter((work) => work.tags.includes(item)));
      }
    }, 500);
  };

  // Helper: avoid rendering broken anchors if a link is missing
  const isNonEmptyString = (val) => typeof val === "string" && val.trim().length > 0;

  const openCaseStudy = (work) => {
    setActiveCaseStudy(work);
    setIsCaseStudyOpen(true);
  };

  const closeCaseStudy = () => {
    setIsCaseStudyOpen(false);
    setActiveCaseStudy(null);
  };

  return (
  <>
    <h2 className="head-text">
      Selected Professional <span>Projects</span>
    </h2>

    <div className="app__work-filter">
      {[
        "All",
        "NEXT JS",
        "Web App",
        "React JS",
        "ASP.NET",
        "PostgreSQL",
        "Docker",
        "Enterprise",
      ].map((item, index) => (
        <div
          key={index}
          onClick={() => handleWorkFilter(item)}
          className={`app__work-filter-item app__flex p-text ${
            activeFilter === item ? "item-active" : ""
          }`}
        >
          {item}
        </div>
      ))}
    </div>

    <motion.div
      animate={animateCard}
      transition={{ duration: 0.5, delayChildren: 0.5 }}
      className="app__work-portfolio"
    >
      {filterWork.map((work, index) => {
        const isConfidential = Boolean(work?.isConfidential);

        const eyeHref = work?.projectLink; // keep existing field
        const githubHref = work?.codeLink; // keep existing field (personal projects)

        const eyeLabel = isConfidential ? "Public Experience" : "View Project";
        const githubLabel = isConfidential ? "Case Study" : "View Code";

        return (
          <div className="app__work-item app__flex" key={index}>
            <div className="app__work-img app__flex">
              <img src={urlFor(work.imgUrl)} alt={work.name} />

              {/* Confidential badge (only when flagged) */}
              {isConfidential && (
                <div className="app__work-confidential-badge">Confidential</div>
              )}

              <motion.div
                whileHover={{ opacity: [0, 1] }}
                transition={{
                  duration: 0.25,
                  ease: "easeInOut",
                  staggerChildren: 0.5,
                }}
                className="app__work-hover app__flex"
              >
                {/* Eye / Public Experience (unchanged behavior) */}
                {isNonEmptyString(eyeHref) && (
                  <a
                    href={eyeHref}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={eyeLabel}
                    title={eyeLabel}
                  >
                    <motion.div
                      whileInView={{ scale: [0, 1] }}
                      whileHover={{ scale: [1, 0.9] }}
                      transition={{ duration: 0.25 }}
                      className="app__flex"
                    >
                      <AiFillEye />
                    </motion.div>
                  </a>
                )}

                {/* GitHub / Case Study */}
                {isConfidential ? (
                  // Confidential: open modal (no external link)
                  <motion.button
                    type="button"
                    whileInView={{ scale: [0, 1] }}
                    whileHover={{ scale: [1, 0.9] }}
                    transition={{ duration: 0.25 }}
                    className="app__work-hover-btn app__flex"
                    onClick={() => openCaseStudy(work)}
                    aria-label={githubLabel}
                    title={githubLabel}
                  >
                    <AiFillGithub />
                  </motion.button>
                ) : (
                  // Non-confidential: keep existing behavior (link to codeLink)
                  isNonEmptyString(githubHref) && (
                    <a
                      href={githubHref}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={githubLabel}
                      title={githubLabel}
                    >
                      <motion.div
                        whileInView={{ scale: [0, 1] }}
                        whileHover={{ scale: [1, 0.9] }}
                        transition={{ duration: 0.25 }}
                        className="app__flex"
                      >
                        <AiFillGithub />
                      </motion.div>
                    </a>
                  )
                )}
              </motion.div>
            </div>

            <div className="app__work-content app__flex">
              <h4 className="bold-text">{work.title}</h4>
              <p className="p-text" style={{ marginTop: 10 }}>
                {work.description}
              </p>

              <div className="app__work-tag app__flex">
                <p className="p-text">{work.tags[0]}</p>
              </div>
            </div>
          </div>
        );
      })}
    </motion.div>

    {/* Case Study Modal (only used for confidential items) */}
    <CaseStudyModal
      isOpen={isCaseStudyOpen}
      onClose={closeCaseStudy}
      work={activeCaseStudy}
    />
  </>
);
};

export default AppWrap(
  MotionWrap(Work, "app__works"),
  "work",
  "app__primarybg"
);